import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { protectedResources } from '../auth/auth.config';
import { PartDto, PartListDto } from '../models/device.model';
import {
  PartId,
  PartWithCounterDetails,
  PartWithCounterDetailsAllOfCounters,
  PartWithCounterDetailsForTable,
} from '../models/parts-info.model';

@Injectable({
  providedIn: 'root',
})
export class PartListService {
  apiEndpoint: string = protectedResources.apiTodoList.endpoint;

  constructor(private readonly http: HttpClient) {}

  getPartsList(): Observable<PartListDto> {
    const headers = new HttpHeaders({
      Accept: 'application/vnd.web.parts.with.metrics+json',
    });
    return this.http.get<PartListDto>(`${this.apiEndpoint}/parts`, { headers });
  }

  getPartsListForTable(): Observable<PartWithCounterDetailsForTable[]> {
    return this.getPartsList().pipe(
      map((response: PartListDto) => {
        return (response.parts ?? []).map((part) => {
          // Compute percentage for each counter and find the highest percentage counter
          const updatedCounters = (part.metrics ?? []).map((metric) => {
            const counterValue = Number(metric.value);
            const counterThreshold = Number(metric.valueThreshold);
            const percentage =
              Number.isFinite(counterValue) &&
              Number.isFinite(counterThreshold) &&
              counterThreshold > 0
                ? parseFloat(
                    ((counterValue / counterThreshold) * 100).toFixed(2),
                  )
                : undefined;

            return {
              type: metric.type,
              value:
                metric.value === null || metric.value === undefined
                  ? ''
                  : String(metric.value),
              threshold:
                metric.valueThreshold === null ||
                metric.valueThreshold === undefined
                  ? ''
                  : String(metric.valueThreshold),
              unit: metric.unit,
              valueType: this.mapMetricUnit(metric.unit),
              percentage,
            };
          });

          // Find the counter with the highest percentage
          const counterToShow = updatedCounters.some(
            (counter) => counter.percentage !== undefined,
          )
            ? updatedCounters.reduce((max, counter) => {
                if (counter.percentage === undefined) {
                  return max;
                }
                return max.percentage === undefined ||
                  counter.percentage > max.percentage
                  ? counter
                  : max;
              })
            : {
                type: '',
                value: '',
                threshold: '',
                percentage: 0,
              };

          return {
            ...this.mapPartDetails(part),
            counters: updatedCounters,
            counterToDisplay: counterToShow,
            lastConnectedDevice: part.lastConnectedDevice,
          };
        });
      }),
    );
  }

  private mapPartDetails(part: PartDto): PartWithCounterDetails {
    const details =
      part.details?.type === 'QsUavPart' ? part.details : undefined;

    return {
      type: part.type ?? PartId.PartTypeEnum.Other,
      partId: {
        id: details?.partId ?? part.partId ?? '',
        partType: part.type ?? PartId.PartTypeEnum.Other,
        partSubType: details?.partSubType ?? '',
      },
      qsUavPartType: details?.partType,
    };
  }

  private mapMetricUnit(
    unit?: string,
  ): PartWithCounterDetailsAllOfCounters.ValueTypeEnum {
    const normalizedUnit = unit?.toLowerCase();
    const supportedUnits = Object.values(
      PartWithCounterDetailsAllOfCounters.ValueTypeEnum,
    );

    return supportedUnits.includes(
      normalizedUnit as PartWithCounterDetailsAllOfCounters.ValueTypeEnum,
    )
      ? (normalizedUnit as PartWithCounterDetailsAllOfCounters.ValueTypeEnum)
      : PartWithCounterDetailsAllOfCounters.ValueTypeEnum.Unknown;
  }
}
